import { FormattedMessage } from 'react-intl';
import { useInfiniteScroll } from '@_/hooks';
import { buttonCommonI18n } from '@_/i18n-common';
import { Button, LoadingDots } from '@_/ui';

interface BaseLoadMoreTriggerProps {
  className?: string;
  disabled?: boolean;
  hasMore: boolean;
  loading: boolean;
  onFetchMore: () => void;
  renderLoading?: () => React.ReactNode;
}

type LoadMoreTriggerProps =
  | ({
      trigger: 'button';
      renderButton?: (props: { onClick: () => void }) => React.ReactNode;
    } & BaseLoadMoreTriggerProps)
  | ({
      trigger: 'scroll';
      scrollMargin?: number;
    } & BaseLoadMoreTriggerProps);

export function LoadMoreTrigger(props: LoadMoreTriggerProps) {
  const {
    disabled = false,
    hasMore,
    loading,
    onFetchMore,
    renderLoading = renderDefaultLoading
  } = props;

  const scrollTriggerRef = useInfiniteScroll({
    disabled,
    hasMore,
    isLoading: loading,
    margin: props.trigger === 'scroll' ? props.scrollMargin : undefined,
    onFetchMore
  });

  if (props.trigger === 'button') {
    const { renderButton = renderDefaultButton } = props;

    return (
      <div className={props.className}>
        {loading && renderLoading()}

        {hasMore && !loading && !disabled && renderButton({ onClick: onFetchMore })}
      </div>
    );
  }

  if (loading || hasMore) {
    return (
      <div ref={scrollTriggerRef} className={props.className}>
        {renderLoading()}
      </div>
    );
  }
}

function renderDefaultButton({ onClick }: { onClick: () => void }) {
  return (
    <div className="flex justify-center">
      <Button onClick={onClick} size="sm">
        <FormattedMessage {...buttonCommonI18n.showMore} />
      </Button>
    </div>
  );
}

function renderDefaultLoading() {
  return <LoadingDots />;
}
